'use client';

import { type StockItemResult } from '@app/(darkHeader)/ai/_shared/_actions/searchLibraryImages';
import { ImageResultsCard } from '@app/(darkHeader)/ai/_shared/_components/ImageResultsCard/ImageResultsCard';
import { useAiToolkitContext } from '@app/(darkHeader)/ai/_shared/_context/AiToolkitContext';
import { SearchLayout } from '@app/(darkHeader)/ai/_shared/_layouts/SearchLayout';
import { type JSX } from 'react';

import { Icon } from '@videoblocks/design-system';

import { useVideoGenerationForm } from '../../useVideoGenerationForm';
import { CurrentVideoGenerationCta } from './CurrentVideoGenerationCta';

export const ImageSearchLayout = (): JSX.Element => {
    const { imageSearch, activeTask } = useAiToolkitContext();
    const { results, isLoading, error, hasSearched, currentPage, totalPages, goToPage } = imageSearch;
    const { formState, onLibraryImageSelect, onFileRemove } = useVideoGenerationForm();
    const { selectedLibraryImage } = formState;
    const isFormDisabled = !!activeTask && activeTask.status === 'inProgress';

    const handleSelect = (item: StockItemResult): void => {
        if (isFormDisabled) return;
        if (item.contentId === selectedLibraryImage?.contentId) {
            onFileRemove();
            return;
        }
        onLibraryImageSelect(item);
    };

    return (
        <SearchLayout
            results={results}
            isLoading={isLoading}
            error={error}
            hasSearched={hasSearched}
            currentPage={currentPage}
            totalPages={totalPages}
            goToPage={goToPage}
            noResultsTitle="No matching images found"
            noResultsIcon={<Icon name="imageModified" width={32} aria-hidden="true" className="dark:[&_path]:fill-neutral-dark-1000" />}
            banner={<CurrentVideoGenerationCta />}
            dataTestId="image-search-layout"
            renderCard={(item, aspectRatio) => (
                <ImageResultsCard
                    imageUrl={item.thumbnailUrl}
                    imageAlt={item.title}
                    selected={item.contentId === selectedLibraryImage?.contentId}
                    disabled={isFormDisabled}
                    onSelect={() => handleSelect(item)}
                    style={{ aspectRatio }}
                />
            )}
        />
    );
};
